import CheckBotEstablishedKks from '@/components/CheckBotEstablishedKks/CheckBotEstablishedKks.vue';
import { endpointsList } from '@/helpers';
import { GlobalUtils } from '@/mixins/globalUtils';
import axios from 'axios';
import {
  DEFAULT_METADATA_VALUES_SEPARATOR,
  IDigitalizationDocumentDetails,
  ISupplierDocumentDetails,
  IKksValidationResultDto,
  ProjectType,
  Serialized,
} from 'types';
import { deduplicateArray } from 'utils';
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'CheckBotEstablishedKksPanel',
  components: { CheckBotEstablishedKks },
  props: {
    digitalizationDocument: {
      type: [
        Object as () => Serialized<IDigitalizationDocumentDetails>,
        () => undefined,
      ],
      required: false,
    },
    file: {
      type: [
        Object as () => Serialized<ISupplierDocumentDetails>,
        () => undefined,
      ],
      required: false,
    },
    isActive: {
      type: Boolean,
      required: true,
    },
    isValidatingKks: {
      type: Boolean,
      required: true,
    },
    kksValidationList: {
      type: Array as () => IKksValidationResultDto[],
      required: true,
    },
    mode: {
      type: String as () => ProjectType,
      required: true,
    },
  },
  emits: ['document-data-changed'],
  data() {
    return {
      isCopyingKks: false,
    };
  },
  methods: {
    async moveCheckBotEstablishedKksNumbersToMainKksField(
      onlyValidated: boolean,
    ): Promise<void> {
      try {
        this.isCopyingKks = true;
        if (this.mode === ProjectType.SUPPLIER_PROJECT) {
          await axios.patch(
            endpointsList.checkBotKks.moveCheckBotEstablishedKksCodesToMainKksField(
              this.file!._id.toString(),
              onlyValidated,
            ),
          );
        } else if (this.mode === ProjectType.DIGITALIZATION_PROJECT) {
          await axios.patch(
            endpointsList.checkBotKks.moveCheckBotEstablishedKksCodesToMainKksField(
              this.digitalizationDocument!._id.toString(),
              onlyValidated,
            ),
          );
        }

        await this.updateDocumentData();

        (this as unknown as GlobalUtils).createToast(
          this.$t('documentDetailView.checkBotEstablishedKksCopied.short'),
          this.$t('documentDetailView.checkBotEstablishedKksCopied.long', 2),
          'success',
        );
      } catch {
        (this as unknown as GlobalUtils).createToast(
          this.$t('errors.error'),
          this.$t('documentDetailView.errors.errorCopyingEstablishedKks.long'),
          'danger',
        );
      } finally {
        this.isCopyingKks = false;
      }
    },

    async moveOneCheckBotEstablishedKksToMainKksField(
      kks: string,
    ): Promise<void> {
      try {
        if (this.mode === ProjectType.SUPPLIER_PROJECT) {
          await axios.patch(
            endpointsList.checkBotKks.moveOneCheckBotEstablishedKksToMainKksField(
              this.file!._id.toString(),
              kks,
            ),
          );
        } else if (this.mode === ProjectType.DIGITALIZATION_PROJECT) {
          await axios.patch(
            endpointsList.checkBotKks.moveOneCheckBotEstablishedKksToMainKksField(
              this.digitalizationDocument!._id.toString(),
              kks,
            ),
          );
        }

        await this.updateDocumentData();

        (this as unknown as GlobalUtils).createToast(
          this.$t('documentDetailView.checkBotEstablishedKksCopied.short'),
          this.$t('documentDetailView.checkBotEstablishedKksCopied.long', 1),
          'success',
        );
      } catch {
        (this as unknown as GlobalUtils).createToast(
          this.$t('errors.error'),
          this.$t('documentDetailView.errors.errorCopyingEstablishedKks.long'),
          'danger',
        );
      }
    },

    async updateDocumentData(): Promise<void> {
      this.$emit('document-data-changed');
    },

    getCheckBotKks() {
      const checkBotKks =
        this.file?.metadata.checkBotEstablishedKKS ??
        this.digitalizationDocument?.fileMetadata.checkBotEstablishedKKS ??
        [];
      const deduplicatedCheckBotKks = deduplicateArray(checkBotKks);
      const currentKks = (
        this.file?.metadata.KKS ?? this.digitalizationDocument?.fileMetadata.KKS
      )?.split(DEFAULT_METADATA_VALUES_SEPARATOR);

      return deduplicatedCheckBotKks.filter(
        (kks) => !currentKks?.includes(kks),
      );
    },
  },
});
